/**
 * A lane's two-week series as numbers, one row per day — the audit view of what
 * the chart draws and what the mobile Rate Check reads.
 *
 * NOTHING IS SMOOTHED OR FILLED. A day below the floor shows "below floor" for
 * its medians and keeps its count and average, exactly as the snapshot holds
 * them, so a gap here is a gap in the file. Each measure has its own count: a
 * flat price without a distance is a total and no rate per mile.
 *
 * IMPLIED MILES is the day's median total over its median $/mi. The two medians
 * come from different histograms, so it is a sanity check, not a distance: on a
 * real lane it should sit near the trip's length, and a figure far off it points
 * at the normalization or the total bands.
 */

import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import { TOTAL_PRICE_CEILING, TOTAL_PRICE_FLOOR, type MarketIntelLaneDay } from '@numeo/types-core'
import { formatNumber } from './shared'
import { money, totalMoney } from './marketIntel'

const BELOW_FLOOR = 'below floor'

/** Only between the open ends: "under $200" or "$15,000+" has no figure to divide. */
function impliedMiles(day: MarketIntelLaneDay): string {
    const total = day.totalP50
    if (total == null || day.p50 == null || day.p50 <= 0 || total <= TOTAL_PRICE_FLOOR || total >= TOTAL_PRICE_CEILING) return '—'
    return formatNumber(Math.round(total / day.p50))
}

export function LaneDaysTable({ days }: { days: MarketIntelLaneDay[] }) {
    if (days.length === 0) return <Typography level="body-sm">No daily series in this snapshot.</Typography>
    // Newest first: an audit starts from today.
    const rows = [...days].sort((a, b) => a.daysAgo - b.daysAgo)
    return (
        <Table size="sm" hoverRow>
            <thead>
                <tr>
                    <th style={{ width: 90 }}>Day</th>
                    <th style={{ textAlign: 'right' }}>Loads ($/mi)</th>
                    <th style={{ textAlign: 'right' }}>Median $/mi</th>
                    <th style={{ textAlign: 'right' }}>Avg $/mi</th>
                    <th style={{ textAlign: 'right' }}>Loads (total)</th>
                    <th style={{ textAlign: 'right' }}>Median total</th>
                    <th style={{ textAlign: 'right' }}>Implied miles</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((d) => (
                    <tr key={d.daysAgo}>
                        <td>{d.daysAgo === 0 ? 'today' : `${d.daysAgo}d ago`}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(d.observations)}</td>
                        <td style={{ textAlign: 'right' }}>{d.observations === 0 ? '—' : d.p50 == null ? BELOW_FLOOR : money(d.p50)}</td>
                        <td style={{ textAlign: 'right' }}>{money(d.avgRpm)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(d.totalObservations)}</td>
                        <td style={{ textAlign: 'right' }}>{d.totalObservations === 0 ? '—' : d.totalP50 == null ? BELOW_FLOOR : totalMoney(d.totalP50)}</td>
                        <td style={{ textAlign: 'right' }}>{impliedMiles(d)}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}
